import { UserProfile, useUser } from '@auth0/nextjs-auth0';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import {
  SiGooglecalendar,
  SiMicrosoft,
  SiMicrosoftoutlook,
} from 'react-icons/si';
import { MdErrorOutline } from 'react-icons/md';
import { ClipLoader } from 'react-spinners';
import { UserInfo } from '../../utils/interfaces';
import {
  ExternalCalendarEventSummary,
  ExternalCalendarRequest,
  ExternalContact,
  ExternalContactsRequest,
  FlexCalSummary,
} from '../../utils/types';
import { isPayingUser } from '../../utils/helpers/userHelper';
import type { ExternalCalendar as ExternalCalendarType } from '../../utils/types';
import ExistingUserCalendars from '../scheduler/flexCal/existingUserCalendars';
import { DateTime } from 'luxon';
import GoogleInstructions from './GoogleInstructions';
import WhyConnect from './whyConnect';
import ReactGA from 'react-ga4';

type Props = {
  flexCalSummary: FlexCalSummary;
  setParticipantEvents: React.Dispatch<
    React.SetStateAction<ExternalCalendarEventSummary[] | undefined>
  >;
  setContacts: React.Dispatch<
    React.SetStateAction<ExternalContact[] | undefined>
  >;
  externalCalendars: ExternalCalendarType[] | undefined;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<boolean>;
};

const ExternalCalendar: FC<Props> = ({
  flexCalSummary,
  setParticipantEvents,
  setContacts,
  externalCalendars,
  isExpanded,
  setIsExpanded,
}) => {
  const { user } = useUser();
  const userInfo = user as UserInfo;

  const [displayExtCalendars, setDisplayExtCalendars] = useState<number[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);
  const [isGoogleConnect, setIsGoogleConnect] = useState<boolean>(false);
  const [showWhyConnect, setShowWhyConnect] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (externalCalendars && externalCalendars.length > 0) {
      const ecIds = externalCalendars
        .filter((ec) => displayExtCalendars.indexOf(ec.id) === -1)
        .map(({ id }) => id);
      setDisplayExtCalendars(ecIds);
    }
  }, [externalCalendars]);

  useEffect(() => {
    let providers: string[] = [];
    if (user && user.sub) {
      if (isPayingUser(userInfo) && externalCalendars) {
        providers = externalCalendars
          .filter((ec) => displayExtCalendars.indexOf(ec.id) === -1)
          .map(({ name }) => name);
      } else {
        providers = ['Default'];
      }
    }
    const getExternalEvents = async () => {
      setIsLoadingEvents(true);
      if (user && user.sub) {
        const request: ExternalCalendarRequest = {
          userName: user.sub,
          startDate: DateTime.now().minus({ days: 1 }).toString(),
          endDate: DateTime.fromISO(flexCalSummary.endDate)
            .endOf('week')
            .minus({ days: 1 })
            .toString(),
          externalProviders: providers,
        };
        const response = await fetch(
          '/api/user/externalCalendars/getExternalCalendarEvents',
          {
            body: JSON.stringify(request),
            method: 'POST',
          }
        );
        const events = await response.json();
        setParticipantEvents(events);
      }
      setIsLoadingEvents(false);
    };
    const getContacts = async () => {
      if (user && user.sub) {
        const request: ExternalContactsRequest = {
          userName: user.sub,
          externalProviders: providers,
        };
        const response = await fetch('/api/user/contacts/getContacts', {
          body: JSON.stringify(request),
          method: 'POST',
        });
        const contacts = await response.json();
        setContacts(contacts);
      }
    };
    getExternalEvents();
    getContacts();
  }, [user, displayExtCalendars]);

  useEffect(() => {
    if (isExpanded) {
      ReactGA.event({
        category: 'Participant View',
        action: 'Connect Calendar Button Clicked',
        label: 'Connect Calendar Button',
      });
    }
  }, [isExpanded]);

  const redirectHref =
    typeof window !== 'undefined' && window.location.pathname
      ? window.location.pathname
      : '';

  const getConnectCalendarText = () => {
    return isPayingUser(userInfo)
      ? `Show ${
          userInfo.userPreferences?.firstName ?? userInfo.nickname
        }'s Availability`
      : 'Compare my Calendar ';
  };

  const toggleShowWhyConnect = () => {
    setShowWhyConnect(true);
  };

  return (
    <>
      <div
        className={`static lg:fixed lg:top-1 lg:right-[10%] z-50 rounded-3xl my-2 lg:m-0 w-full lg:w-1/3 gap-1 lg:gap-3 flex flex-col lg:flex-row-reverse items-center justify-center text-white ${getExpandedStyles(
          isExpanded,
          isPayingUser(userInfo),
          isGoogleConnect,
          user
        )} ${getCardBackground(userInfo)}`}
        onClick={
          //e.stopPropagation();
          isExpanded
            ? undefined
            : () =>
                user === undefined
                  ? setIsExpanded(true)
                  : !isPayingUser(userInfo)
                  ? undefined
                  : setIsExpanded(true)
        }
      >
        {isExpanded && !isGoogleConnect ? (
          <div className="flex flex-col items-center h-full gap-3 pt-0 lg:pt-5">
            <button
              onClick={() => setIsExpanded(false)}
              className="block pt-1 justify-self-start lg:hidden"
            >
              <FaChevronUp size={20} />
            </button>
            {isPayingUser(userInfo) ? (
              <ExistingUserCalendars
                externalCalendars={externalCalendars}
                displayExtCalendars={displayExtCalendars}
                setDisplayExtCalendars={setDisplayExtCalendars}
                redirectHref={redirectHref}
                loading={isLoadingEvents}
              />
            ) : (
              <>
                <button
                  onClick={() => setIsGoogleConnect(true)}
                  className="flex flex-row items-center w-64 h-10 bg-white rounded-md drop-shadow-xl hover:cursor-pointer"
                >
                  <FcGoogle size={18} className="ml-2 mr-6" />
                  <p className="text-black">Connect With Google</p>
                </button>
                <Link
                  href={`/api/auth/login?provider=waad2&redirect-url=${redirectHref}`}
                >
                  <div className="flex flex-row items-center w-64 h-10 bg-white rounded-md drop-shadow-xl hover:cursor-pointer">
                    <SiMicrosoft
                      size={18}
                      className="text-[#0078D4] mr-6 ml-2"
                    />
                    <p className="text-black">Connect With Microsoft</p>
                  </div>
                </Link>
                <p className="text-sm lg:mb-0 -mb-1">
                  Confirmed Subscriber?{' '}
                  <Link
                    href={`/api/auth/login?redirect-url=${redirectHref}&redirect-to-current=true`}
                  >
                    <a className="underline text-confirmed-primary">Log in</a>
                  </Link>
                </p>
                <p className="lg:text-xs -mb-4 lg:-mb-3 text-[9px]">
                  {'Your events will NOT be shared with ' +
                    flexCalSummary.displayName}
                </p>
                <p className="lg:text-xs text-[9px]">
                  By choosing to connect you agree to our{' '}
                  <a
                    href="https://www.confirmedapp.com/privacy"
                    target="_blank"
                    className="underline text-confirmed-primary"
                  >
                    Terms & Conditions
                  </a>
                </p>
              </>
            )}
            <button
              onClick={() => setIsExpanded(false)}
              className="hidden pt-1 lg:block"
            >
              <FaChevronUp size={20} />
            </button>
          </div>
        ) : (
          isPayingUser(userInfo) && (
            <>
              <i>
                <FaChevronDown size={20} className="block lg:hidden" />
                <FaChevronDown size={20} className="hidden lg:block" />
              </i>
              <p className="text-lg">{getConnectCalendarText()}</p>
            </>
          )
        )}
        {!isExpanded && user === undefined && (
          <>
            <div className="lg:flex items-center justify-items-center hidden">
              <div className="w-12 block lg:hidden"></div>
              <i className="lg:-ml-4">
                <FaChevronDown size={20} className="block lg:hidden w-12" />
                <FaChevronDown size={20} className="hidden lg:block w-12" />
              </i>
              <span
                className="bg-confirmed-primary rounded-lg px-1 lg:p-1 hover:cursor-pointer text-black sm:justify-end w-12 justify-self-center mb-1 lg:mb-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleShowWhyConnect();
                }}
              >
                Why?
              </span>
            </div>
            <p className="text-lg">{getConnectCalendarText()} </p>
            <div className="flex items-center justify-items-center lg:hidden">
              <div className="w-12 block lg:hidden"></div>
              <i className="lg:-ml-4">
                <FaChevronDown size={20} className="block lg:hidden w-12" />
                <FaChevronDown size={20} className="hidden lg:block w-12" />
              </i>
              <span
                className="bg-confirmed-primary rounded-lg px-1 lg:p-1 hover:cursor-pointer text-black sm:justify-end w-12 justify-self-center mb-1 lg:mb-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleShowWhyConnect();
                }}
              >
                Why?
              </span>
            </div>
          </>
        )}
        {!isPayingUser(userInfo) && (
          <div className="flex flex-row items-center gap-4">
            {isLoadingEvents ? (
              <ClipLoader size={25} color={'#FFFFFF'} className="m-auto" />
            ) : (
              <>
                {getCalendarIcon(userInfo)}
                {getText(userInfo)}
              </>
            )}
          </div>
        )}
        {isGoogleConnect && (
          <GoogleInstructions
            redirectHref={redirectHref}
            setIsGoogleConnect={setIsGoogleConnect}
          />
        )}
      </div>
      {showWhyConnect && <WhyConnect setShowWhyConnect={setShowWhyConnect} />}
    </>
  );
};

export default ExternalCalendar;

const getCardBackground = (userInfo: UserInfo) => {
  if (userInfo?.sub === undefined || userInfo?.sub === null)
    return 'bg-confirmed-secondary';
  if (typeof userInfo?.registerResponse !== 'string' && userInfo?.isSignUp)
    return 'bg-red-600';
  if (userInfo?.sub.includes('waad')) return 'bg-[#0078D4]';
  if (userInfo?.sub.includes('google')) return 'bg-yellow-800';
  return 'bg-confirmed-secondary';
};

const getCalendarIcon = (userInfo: UserInfo) => {
  if (userInfo?.sub === undefined || userInfo?.sub === null) return <></>;
  if (typeof userInfo?.registerResponse !== 'string' && userInfo?.isSignUp)
    return <MdErrorOutline size={30} />;
  if (userInfo?.sub.includes('waad')) return <SiMicrosoftoutlook size={30} />;
  if (userInfo?.sub.includes('google')) return <SiGooglecalendar size={30} />;
  return <></>;
};

const getText = (userInfo: UserInfo) => {
  if (userInfo?.sub === undefined || userInfo?.sub === null) return '';
  if (typeof userInfo?.registerResponse !== 'string' && userInfo?.isSignUp)
    return <p className="text-xl">Error connecting calendar</p>;
  if (userInfo?.sub.includes('waad') || userInfo?.sub.includes('google'))
    return (
      <p className="text-sm lg:text-xl">
        Connected to {userInfo.userPreferences?.firstName}'s calendar
      </p>
    );
  return <></>;
};

const getExpandedStyles = (
  isExpanded: boolean,
  isPayingUser: boolean,
  isGoogleConnect: boolean,
  user: UserProfile | undefined
) => {
  if (isExpanded && isPayingUser) return 'h-[28vh] lg:h-[240px]';
  if (isExpanded && isGoogleConnect) return 'h-[30vh] lg:h-[240px]';
  if (isExpanded && !isPayingUser) return 'h-[24vh] lg:h-[200px]';
  if (!isPayingUser && !user) return 'h-[8vh] lg:h-[62px] hover:cursor-pointer';
  if (isPayingUser) return 'h-[8vh] lg:h-[62px] hover:cursor-pointer';
  return 'h-[8vh] lg:h-[62px]';
};
